import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { ChatMessage } from '../lib/types'

interface MessagesResponse {
  messages: ChatMessage[]
}
interface MessageResponse {
  message: ChatMessage
}

/**
 * Group chat — the per-group `messages` stream where recs land and feedback is
 * captured (build-contract §1.1, design-system §6.6).
 *
 * NOTE TO INTEGRATOR: §3 of the build contract does not yet pin a messages
 * endpoint. This hook targets the conventional
 *   GET  /api/v1/groups/:id/messages  -> { messages: [...] }
 *   POST /api/v1/groups/:id/messages  -> { message: {...} }
 * and degrades gracefully (treats 404 as "chat not wired yet") so the screen
 * never breaks the zero-credential boot. See the "notes" handoff.
 */
export function useMessages(groupId: string | undefined) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [available, setAvailable] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  const refresh = useCallback(() => {
    if (!groupId) return Promise.resolve()
    return api
      .get<MessagesResponse>(`/groups/${groupId}/messages`)
      .then((data) => {
        setMessages(data.messages ?? [])
        setAvailable(true)
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 404) setAvailable(false)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [groupId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const send = useCallback(
    async (body: string) => {
      if (!groupId || !body.trim()) return
      try {
        const data = await api.post<MessageResponse>(`/groups/${groupId}/messages`, { body })
        if (data.message) setMessages((prev) => [...prev, data.message])
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) setAvailable(false)
      }
    },
    [groupId],
  )

  return { messages, available, isLoading, refresh, send }
}
